import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import cs from 'classnames';

import { deleteServices } from 'api/Service';

import { pluralize } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

const BulkActions = ({ selectedRows, onClearSelection }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();
  const { id: practiceId } = useParams();

  const totalSelected = selectedRows?.length ?? 0;
  const label = pluralize('service', totalSelected);

  const { mutate: bulkDeleteServices, isLoading: isDeleting } = useCustomMutation({
    mutationFn: (params) => deleteServices(navigate, params),
    invalidateQueryKey: 'services',
    successMessage: `${totalSelected} ${label} removed successfully`,
    errorMessage: `Unable to remove ${label}`,
    onSuccess: () => {
      onClearSelection();
      setShowConfirmation(false);
    }
  });

  const handleDelete = () => {
    const serviceIds = selectedRows.map((row) => row?.data?.id).filter(Boolean);
    if (serviceIds.length === 0) {
      setShowConfirmation(false);
      return;
    }
    bulkDeleteServices({ serviceIds, practiceId: Number(practiceId) });
  };

  const isVisible = totalSelected > 0 && !showConfirmation;

  return (
    <>
      <div
        className={cs(
          'fixed bottom-[123px] h-[48px]',
          'flex items-center justify-between gap-4 px-4 py-3',
          'z-40 rounded-lg bg-neutral-700',
          'transition-all duration-300 ease-out',
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0'
        )}
        style={{
          left: 'calc(320px + 40px + 24px + (100vw - 1168px) / 2)',
          right: 'calc(24px + (100vw - 1168px) / 2)'
        }}>
        <div className="flex items-center gap-3">
          <Icon
            icon="close-linear"
            color="white"
            size={16}
            onClick={onClearSelection}
            className="cursor-pointer rounded hover:opacity-70"
          />
          <span className="text-sm font-500 text-white">
            {totalSelected} {label} selected
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            text={`Remove ${label}`}
            icon="trash-mono"
            size="small"
            color="danger"
            onClick={() => setShowConfirmation(true)}
          />
        </div>
      </div>
      <Confirm
        isOpen={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        title={`Remove ${totalSelected} ${label}`}
        message={`Are you sure you want to remove the selected ${label}? This action cannot be undone.`}
        icon="trash-mono"
        variant="danger"
        footerActions={{
          primary: {
            text: `Remove ${label}`,
            onClick: handleDelete,
            color: 'danger',
            isLoading: isDeleting,
            isDisabled: isDeleting
          },
          secondary: { text: 'Cancel' }
        }}
      />
    </>
  );
};

export default BulkActions;
